<script lang="ts">
	import PageHeader from '$lib/components/PageHeader.svelte';
	import CodeBlock from '$lib/components/CodeBlock.svelte';
	import Pill from '$lib/components/Pill.svelte';
	import { ArrowRight } from '@lucide/svelte';

	const toc = [
		{ id: 'query-model', label: 'Labels are the query model' },
		{ id: 'principles', label: 'Six principles' },
		{ id: 'starter', label: 'A starter schema' },
		{ id: 'smells', label: 'How schemas fail' },
		{ id: 'lineage', label: 'The Prometheus lineage' }
	];

	const principles = [
		{
			title: "Label what you'll query by",
			body: "A label earns its place by being grouped or filtered on. If you'd never break a report down by it or filter an export on it, it isn't a label — it's a note. Moment Tally has notes; use them for the color, and keep labels for the axes."
		},
		{
			title: 'Cardinality is the budget',
			body: 'Every distinct value a key takes is one more bucket in every report that groups by it. Values should come from a small, slowly growing vocabulary. Unbounded values — ticket titles, timestamps, one-off prose — degenerate grouping into one span per bucket, and your reports stop compressing anything.'
		},
		{
			title: 'One fact per key',
			body: "Don't pack repo, feature, and kind of work into a single value. Three separate keys filter and join independently; a compound value can only ever be matched whole, and you'll be splitting strings in a spreadsheet within a month."
		},
		{
			title: 'Few, stable keys, consistently named',
			body: 'Pick repo once — not proj on Mondays and project on Thursdays. Renaming a key later severs your history in every query that uses it. Label Review can rescue a drifted schema, but a schema that never drifts needs no rescue.'
		},
		{
			title: "Mirror the system you'll join against",
			body: "If you plan to line time up with source control, use your code host's exact naming: repo: sfi/moment-tally, not repo: momenttally. Joins are literal — a value that's merely close matches nothing."
		},
		{
			title: 'Decide what unlabeled means',
			body: 'Absent is a value too. Decide what a span with no client — or no repo — means in your reports (internal? overhead? unbilled?) and apply it consistently, so the gaps in your data carry information instead of doubt.'
		}
	];

	const smells = [
		{
			label: 'task: fix-flaky-ci-on-auth-service-again',
			smell: 'Unbounded values',
			consequence:
				'Every span invents a new value, so grouping by task yields one span per bucket — the report is just your log, re-sorted. This is a note wearing a label costume.'
		},
		{
			label: 'work: moment-tally-auth-bugfix',
			smell: 'Three facts fused into one key',
			consequence:
				'Repo, area, and type are welded together, so you can never ask "all bugfix time, across repos" without string surgery. Split it: repo, area, type.'
		},
		{
			label: 'proj: momenttally  ·  project: moment-tally',
			smell: 'Drifting key names and spellings',
			consequence:
				'Half your history lands under each spelling. Every query needs an OR, and totals silently miss whichever variant you forget.'
		},
		{
			label: 'repo: momenttally',
			smell: 'Approximate naming',
			consequence:
				'Your code host calls it sfi/moment-tally. A close-but-not-exact value matches nothing when you join time to commits and PRs — the whole point of the key.'
		}
	];

	const starterSchema = `# four keys, each drawing from a small, stable vocabulary
repo:    sfi/moment-tally   # exact source-control path — joins against commits & PRs
feat:    label-review       # the feature or stream of work
type:    review             # build | review | support | ops
client:  acme               # who the hours belong to`;
</script>

<svelte:head>
	<title>What makes a good labeling schema — Moment Tally</title>
	<meta
		name="description"
		content="Labels are Moment Tally's whole query model. Six principles for a schema that keeps working — bounded cardinality, one fact per key, stable naming — plus a starter schema and the failure modes to avoid."
	/>
</svelte:head>

<PageHeader
	eyebrow="Documentation · Labels"
	title="What makes a good labeling schema"
	lead="Labels are Moment Tally's whole query model — reports, export filters, and the source-control join are only as good as the schema behind them. These principles come from running Prometheus metrics at scale, where label schemas succeed and collapse for exactly the same reasons."
/>

<div class="mx-auto grid max-w-6xl gap-12 px-5 py-16 lg:grid-cols-[220px_1fr]">
	<!-- TOC -->
	<aside class="hidden lg:block">
		<nav class="sticky top-24 space-y-1 border-l border-border pl-4">
			<p class="mb-3 font-mono text-xs uppercase tracking-widest text-fg-faint">On this page</p>
			{#each toc as t (t.id)}
				<a
					href="#{t.id}"
					class="block py-1 text-sm text-fg-muted transition-colors hover:text-accent"
				>
					{t.label}
				</a>
			{/each}
		</nav>
	</aside>

	<article class="min-w-0 space-y-16">
		<section id="query-model" class="scroll-mt-24">
			<h2 class="font-display text-2xl font-bold">Labels are the query model</h2>
			<p class="mt-3 leading-relaxed text-fg-muted">
				There is no folder tree in Moment Tally — a span <span class="text-fg">is</span> its labels. Every
				question you'll ever ask of your history ("hours per client this quarter", "review share by repo",
				"which spans map to that shipped PR?") is a group-by or a filter over label keys. That makes the
				schema — which keys exist, what values they take — the highest-leverage decision you make. Good
				news: a good schema is small, and the rules fit on one page.
			</p>
		</section>

		<section id="principles" class="scroll-mt-24">
			<h2 class="font-display text-2xl font-bold">Six principles</h2>
			<div class="mt-6 grid gap-4 sm:grid-cols-2">
				{#each principles as p, i (p.title)}
					<div class="card p-5">
						<p class="font-mono text-xs text-accent">{String(i + 1).padStart(2, '0')}</p>
						<h3 class="mt-1 font-display font-bold">{p.title}</h3>
						<p class="mt-2 text-sm leading-relaxed text-fg-muted">{p.body}</p>
					</div>
				{/each}
			</div>
		</section>

		<section id="starter" class="scroll-mt-24">
			<h2 class="font-display text-2xl font-bold">A starter schema</h2>
			<p class="mt-3 leading-relaxed text-fg-muted">
				Four keys cover most solo and small-team work. Each holds one fact, each value comes from a
				vocabulary you could list from memory, and <span class="font-mono text-fg">repo</span> uses your
				code host's exact naming so exports join cleanly against commits and PRs.
			</p>
			<CodeBlock lang="labels" code={starterSchema} />
			<div class="card mt-2 flex flex-wrap items-center gap-2 p-5">
				<Pill tone="red">repo: sfi/moment-tally</Pill>
				<Pill tone="blue">feat: label-review</Pill>
				<Pill tone="green">type: review</Pill>
				<Pill tone="amber">client: acme</Pill>
				<span class="ml-auto font-mono text-xs text-fg-faint">one span · four axes</span>
			</div>
			<p class="mt-4 leading-relaxed text-fg-muted">
				What this buys you: hours per <span class="font-mono text-fg">client</span> across every
				repo, <span class="font-mono text-fg">type: review</span> share per repo, and a span-to-PR
				join for the
				<a class="text-accent hover:underline" href="/case-study/joined-to-what-shipped"
					>time-joined-to-what-shipped</a
				> workflow — none of which required deciding a hierarchy up front. Start smaller if in doubt;
				a key is easy to add and painful to rename.
			</p>
		</section>

		<section id="smells" class="scroll-mt-24">
			<h2 class="font-display text-2xl font-bold">How schemas fail</h2>
			<p class="mt-3 leading-relaxed text-fg-muted">
				The same schema, gone wrong — each of these looks harmless on the day you type it, and each
				quietly breaks a query you'll want later.
			</p>
			<div class="mt-6 space-y-4">
				{#each smells as s (s.label)}
					<div class="card border-l-2 border-l-pill-red/70 p-5">
						<p class="overflow-x-auto whitespace-nowrap font-mono text-sm text-fg">{s.label}</p>
						<p class="mt-2 text-sm font-semibold text-fg">{s.smell}</p>
						<p class="mt-1 text-sm leading-relaxed text-fg-muted">{s.consequence}</p>
					</div>
				{/each}
			</div>
			<div class="card mt-6 border-l-2 border-l-accent p-4 text-sm text-fg-muted">
				<span class="font-semibold text-fg">Already drifted?</span> Label Review can merge variant keys
				and retire noisy values after the fact — your history is editable, not carved in stone. But every
				rescue is a query you couldn't run in the meantime.
			</div>
		</section>

		<section id="lineage" class="scroll-mt-24">
			<h2 class="font-display text-2xl font-bold">The Prometheus lineage</h2>
			<p class="mt-3 leading-relaxed text-fg-muted">
				None of this guidance is original, and that's its strength. Moment Tally's label system is
				modeled on <span class="text-fg">Prometheus</span> metric labels, and these principles are
				the operational lessons of running Prometheus at scale: high-cardinality labels explode
				timeseries the way they drown reports; compound values resist aggregation; renaming a label
				severs history. Time tracking hits the same walls for the same reasons — so we wrote down
				the same rules. More on what Moment Tally borrows, and from whom, on the
				<a class="text-accent hover:underline" href="/attributions">attributions</a> page.
			</p>
			<div class="mt-8 flex flex-wrap gap-3">
				<a
					href="/case-study/joined-to-what-shipped"
					class="btn-accent inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-105"
				>
					See the schema at work <ArrowRight size={15} />
				</a>
				<a
					href="/docs#label"
					class="rounded-full border border-border-strong px-5 py-2.5 text-sm font-medium text-fg-muted transition-colors hover:border-accent hover:text-fg"
				>
					Back to the docs
				</a>
			</div>
		</section>
	</article>
</div>
